'use client';

import { useNavigationProgressContextSafe } from '@/contexts/NavigationProgressContext';

/**
 * Hook to access navigation progress state.
 * Safe to use anywhere - returns { isNavigating: false } if outside provider.
 *
 * The navigation progress is triggered by:
 * - `useSafeRouter().push()` / `replace()` / `back()` / `forward()`
 * - `<SafeLink />` component clicks
 *
 * The progress automatically ends when the route changes (pathname or searchParams).
 *
 * @returns {Object} Navigation progress state
 * @returns {boolean} isNavigating - Whether a navigation is currently in progress
 *
 * @example
 * ```tsx
 * function LoadingIndicator() {
 *   const { isNavigating } = useNavigationProgress();
 *   return isNavigating ? <Spinner /> : null;
 * }
 * ```
 */
export const useNavigationProgress = () => {
  const context = useNavigationProgressContextSafe();
  return { isNavigating: context?.isNavigating ?? false };
};
